import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Slider } from '../../../components/ui/slider';
import { Switch } from '../../../components/ui/switch';

interface TemplateColorPickerProps {
  backgroundColor: string;
  onBackgroundColorChange: (color: string) => void;
  
  useGradient: boolean;
  onUseGradientChange: (value: boolean) => void;
  
  backgroundColorEnd?: string;
  onBackgroundColorEndChange?: (color: string) => void;
  
  backgroundAlpha?: number;
  onBackgroundAlphaChange?: (alpha: number) => void;
  
  backgroundAlphaEnd?: number;
  onBackgroundAlphaEndChange?: (alpha: number) => void;
  
  textColor: string;
  onTextColorChange: (color: string) => void;
  
  useTwoColors?: boolean;
  onUseTwoColorsChange?: (value: boolean) => void;
  
  textColorSecondary?: string;
  onTextColorSecondaryChange?: (color: string) => void;

  /** Скрыть контролы цвета текста (фон остаётся) — пост наследует цвет из reel. */
  hideTextColors?: boolean;
}

/**
 * Компонент для выбора цветов шаблона
 * Поддерживает градиенты и прозрачность
 */
export function TemplateColorPicker({
  backgroundColor,
  onBackgroundColorChange,
  useGradient,
  onUseGradientChange,
  backgroundColorEnd,
  onBackgroundColorEndChange,
  backgroundAlpha,
  onBackgroundAlphaChange,
  backgroundAlphaEnd,
  onBackgroundAlphaEndChange,
  textColor,
  onTextColorChange,
  useTwoColors,
  onUseTwoColorsChange,
  textColorSecondary,
  onTextColorSecondaryChange,
  hideTextColors = false,
}: TemplateColorPickerProps) {
  return (
    <div className="space-y-4">
      {/* Переключатель градиента */}
      <div className="flex items-center justify-between">
        <Label htmlFor="useGradient">Использовать градиент</Label>
        <Switch
          id="useGradient"
          checked={useGradient}
          onCheckedChange={onUseGradientChange}
        />
      </div>

      {/* Нижний цвет (или единственный цвет фона) */}
      <div className="space-y-2">
        <Label htmlFor="backgroundColor" className="text-sm text-muted-foreground">
          {useGradient ? 'Нижний цвет' : 'Цвет фона'}
        </Label>
        <div className="flex gap-2 items-center">
          <Input
            id="backgroundColor"
            type="color"
            value={backgroundColor}
            onChange={(e) => onBackgroundColorChange(e.target.value)}
            className="h-10 w-20 cursor-pointer"
          />
          <Input
            type="text"
            value={backgroundColor}
            onChange={(e) => onBackgroundColorChange(e.target.value)}
            placeholder="#FFD700"
            className="flex-1"
          />
        </div>
        
        {/* Без фона */}
        {backgroundAlpha !== undefined && onBackgroundAlphaChange && (
          <div className="flex items-center justify-between">
            <Label className="text-xs text-muted-foreground">Без фона</Label>
            <Switch
              checked={backgroundAlpha === 0}
              onCheckedChange={(checked) => onBackgroundAlphaChange(checked ? 0 : 1)}
            />
          </div>
        )}
      </div>

      {/* Верхний цвет градиента - показывается только если включен градиент */}
      {useGradient && backgroundColorEnd && onBackgroundColorEndChange && (
        <div className="space-y-2">
          <Label htmlFor="backgroundColorEnd" className="text-sm text-muted-foreground">Верхний цвет</Label>
          <div className="flex gap-2 items-center">
            <Input
              id="backgroundColorEnd"
              type="color"
              value={backgroundColorEnd}
              onChange={(e) => onBackgroundColorEndChange(e.target.value)}
              className="h-10 w-20 cursor-pointer"
            />
            <Input
              type="text"
              value={backgroundColorEnd}
              onChange={(e) => onBackgroundColorEndChange(e.target.value)}
              placeholder="#FFD700"
              className="flex-1"
            />
          </div>
          
          {/* Альфа канал для верхнего цвета */}
          {backgroundAlphaEnd !== undefined && onBackgroundAlphaEndChange && (
            <div className="space-y-1">
              <div className="flex items-center justify-between">
                <Label className="text-xs text-muted-foreground">Прозрачность</Label>
                <span className="text-xs text-muted-foreground">{Math.round(backgroundAlphaEnd * 100)}%</span>
              </div>
              <Slider
                value={[backgroundAlphaEnd * 100]}
                onValueChange={(values) => onBackgroundAlphaEndChange(values[0] / 100)}
                min={0}
                max={100}
                step={1}
                className="w-full"
              />
            </div>
          )}
        </div>
      )}

      {/* Цвет текста + два цвета — скрываем для поста (наследует из reel) */}
      {!hideTextColors && (
        <>
          <div className="space-y-2">
            <Label htmlFor="textColor">Цвет текста</Label>
            <div className="flex gap-2 items-center">
              <Input
                id="textColor"
                type="color"
                value={textColor}
                onChange={(e) => onTextColorChange(e.target.value)}
                className="h-10 w-20 cursor-pointer"
              />
              <Input
                type="text"
                value={textColor}
                onChange={(e) => onTextColorChange(e.target.value)}
                placeholder="#000000"
                className="flex-1"
              />
            </div>
          </div>

          {useTwoColors !== undefined && onUseTwoColorsChange && (
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="useTwoColors">Использовать два цвета текста</Label>
                <p className="text-xs text-muted-foreground">Первая строка - основной цвет, остальные - вторичный</p>
              </div>
              <Switch
                id="useTwoColors"
                checked={useTwoColors}
                onCheckedChange={onUseTwoColorsChange}
              />
            </div>
          )}

          {useTwoColors && textColorSecondary && onTextColorSecondaryChange && (
            <div className="space-y-2">
              <Label htmlFor="textColorSecondary">Вторичный цвет текста</Label>
              <div className="flex gap-2 items-center">
                <Input
                  id="textColorSecondary"
                  type="color"
                  value={textColorSecondary}
                  onChange={(e) => onTextColorSecondaryChange(e.target.value)}
                  className="h-10 w-20 cursor-pointer"
                />
                <Input
                  type="text"
                  value={textColorSecondary}
                  onChange={(e) => onTextColorSecondaryChange(e.target.value)}
                  placeholder="#000000"
                  className="flex-1"
                />
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}